// src/utils/lunar-converter.ts

/**
 * Định nghĩa cấu trúc dữ liệu cho ngày Âm lịch và Dương lịch
 * để code dễ đọc và dễ bảo trì hơn.
 */
export interface LunarDate {
  lunarDay: number;
  lunarMonth: number;
  lunarYear: number;
  lunarLeap: boolean;
}

export interface SolarDate {
  day: number;
  month: number;
  year: number;
}

// ========================================================================
// CÁC HÀM TÍNH TOÁN NỘI BỘ (INTERNAL HELPERS)
// ========================================================================

const { floor, sin, PI } = Math;

const computeJulianDayFromDate = (dd: number, mm: number, yy: number): number => {
  const a = floor((14 - mm) / 12);
  const y = yy + 4800 - a;
  const m = mm + 12 * a - 3;
  let jd = dd + floor((153 * m + 2) / 5) + 365 * y + floor(y / 4) - floor(y / 100) + floor(y / 400) - 32045;
  if (jd < 2299161) {
    jd = dd + floor((153 * m + 2) / 5) + 365 * y + floor(y / 4) - 32083;
  }
  return jd;
};

const computeDateFromJulianDay = (jd: number): SolarDate => {
  let a: number, b: number, c: number;
  if (jd > 2299160) {
    a = jd + 32044;
    b = floor((4 * a + 3) / 146097);
    c = a - floor((b * 146097) / 4);
  } else {
    b = 0;
    c = jd + 32082;
  }
  const d = floor((4 * c + 3) / 1461);
  const e = c - floor((1461 * d) / 4);
  const m = floor((5 * e + 2) / 153);
  const day = e - floor((153 * m + 2) / 5) + 1;
  const month = m + 3 - 12 * floor(m / 10);
  const year = b * 100 + d - 4800 + floor(m / 10);
  return { day, month, year };
};

const computeNewMoon = (k: number): number => {
  const t = k / 1236.85;
  const t2 = t * t;
  const t3 = t2 * t;
  const dr = PI / 180;

  let jd1 = 2415020.75933 + 29.53058868 * k + 0.0001178 * t2 - 0.000000155 * t3;
  jd1 = jd1 + 0.00033 * sin((166.56 + 132.87 * t - 0.009173 * t2) * dr);

  const m_ = 359.2242 + 29.10535608 * k - 0.0000333 * t2 - 0.00000347 * t3;
  const mpr = 306.0253 + 385.81691806 * k + 0.0107306 * t2 + 0.00001236 * t3;
  const f = 21.2964 + 390.67050646 * k - 0.0016528 * t2 - 0.00000239 * t3;

  let c1 = (0.1734 - 0.000393 * t) * sin(m_ * dr) + 0.0021 * sin(2 * dr * m_);
  c1 = c1 - 0.4068 * sin(mpr * dr) + 0.0161 * sin(dr * 2 * mpr);
  c1 = c1 - 0.0004 * sin(dr * 3 * mpr);
  c1 = c1 + 0.0104 * sin(dr * 2 * f) - 0.0051 * sin(dr * (m_ + mpr));
  c1 = c1 - 0.0074 * sin(dr * (m_ - mpr)) + 0.0004 * sin(dr * (2 * f + m_));
  c1 = c1 - 0.0004 * sin(dr * (2 * f - m_)) - 0.0006 * sin(dr * (2 * f + mpr));
  c1 = c1 + 0.001 * sin(dr * (2 * f - mpr)) + 0.0005 * sin(dr * (2 * mpr + m_));

  const calculateDeltaT = (t_val: number): number => {
    const t2_val = t_val * t_val;
    const t3_val = t2_val * t_val;
    if (t_val < -11) {
      return 0.001 + 0.000839 * t_val + 0.0002261 * t2_val - 0.00000845 * t3_val - 0.000000081 * t_val * t3_val;
    }
    return -0.000278 + 0.000265 * t_val + 0.000262 * t2_val;
  };

  const jd = jd1 + c1 - calculateDeltaT(t);
  return jd;
};

const getSunLongitude = (jdn: number): number => {
  const t = (jdn - 2451545.0) / 36525;
  const t2 = t * t;
  const dr = PI / 180;
  const m = 357.5291 + 35999.0503 * t - 0.0001559 * t2 - 0.00000048 * t * t2;
  const l0 = 280.46645 + 36000.76983 * t + 0.0003032 * t2;
  let dl = (1.9146 - 0.004817 * t - 0.000014 * t2) * sin(dr * m);
  dl = dl + (0.019993 - 0.000101 * t) * sin(dr * 2 * m) + 0.00029 * sin(dr * 3 * m);
  let l = l0 + dl;
  l = l * dr;
  l = l - PI * 2 * floor(l / (PI * 2));
  return floor(l / (PI / 6));
};

const getNewMoonDay = (k: number, timeZone: number): number => {
  return floor(computeNewMoon(k) + 0.5 + timeZone / 24);
};

const getLunarMonth11 = (yy: number, timeZone: number): number => {
  const off = computeJulianDayFromDate(31, 12, yy) - 2415021;
  const k = floor(off / 29.530588853);
  let nm = getNewMoonDay(k, timeZone);
  const sunLong = getSunLongitude(nm);
  if (sunLong >= 9) {
    nm = getNewMoonDay(k - 1, timeZone);
  }
  return nm;
};

const getLeapMonthOffset = (a11: number, timeZone: number): number => {
  const k = floor((a11 - 2415021.076998695) / 29.530588853 + 0.5);
  let last = 0;
  let i = 1;
  let arc = getSunLongitude(getNewMoonDay(k + i, timeZone));
  do {
    last = arc;
    i++;
    arc = getSunLongitude(getNewMoonDay(k + i, timeZone));
  } while (arc !== last && i < 14);
  return i - 1;
};

// ========================================================================
// CÁC HÀM CHÍNH ĐỂ EXPORT RA BÊN NGOÀI
// ========================================================================

/**
 * Chuyển đổi từ ngày Dương lịch sang ngày Âm lịch.
 * @param dd Ngày dương
 * @param mm Tháng dương
 * @param yy Năm dương
 * @param timeZone Múi giờ (Việt Nam là 7)
 * @returns Object chứa ngày, tháng, năm và tháng nhuận âm lịch
 */
export function convertSolarToLunar(dd: number, mm: number, yy: number, timeZone: number): LunarDate {
  const dayNumber = computeJulianDayFromDate(dd, mm, yy);
  const k = floor((dayNumber - 2415021.076998695) / 29.530588853);
  let monthStart = getNewMoonDay(k + 1, timeZone);
  if (monthStart > dayNumber) {
    monthStart = getNewMoonDay(k, timeZone);
  }
  let a11 = getLunarMonth11(yy, timeZone);
  let b11 = a11;
  let lunarYear: number;
  if (a11 >= monthStart) {
    lunarYear = yy;
    a11 = getLunarMonth11(yy - 1, timeZone);
  } else {
    lunarYear = yy + 1;
    b11 = getLunarMonth11(yy + 1, timeZone);
  }
  const lunarDay = dayNumber - monthStart + 1;
  const diff = floor((monthStart - a11) / 29);
  let lunarLeap = false;
  let lunarMonth = diff + 11;
  if (b11 - a11 > 365) {
    const leapMonthDiff = getLeapMonthOffset(a11, timeZone);
    if (diff >= leapMonthDiff) {
      lunarMonth = diff + 10;
      if (diff === leapMonthDiff) {
        lunarLeap = true;
      }
    }
  }
  if (lunarMonth > 12) {
    lunarMonth -= 12;
  }
  if (lunarMonth >= 11 && diff < 4) {
    lunarYear -= 1;
  }
  return { lunarDay, lunarMonth, lunarYear, lunarLeap };
}

/**
 * Chuyển đổi từ Âm lịch sang Dương lịch
 * @param lunarDay Ngày âm
 * @param lunarMonth Tháng âm
 * @param lunarYear Năm âm
 * @param lunarLeap Có phải tháng nhuận không
 * @param timeZone Múi giờ (Việt Nam là 7)
 * @returns Object ngày tháng dương hoặc null nếu ngày âm không hợp lệ.
 */
export function convertLunarToSolar(lunarDay: number, lunarMonth: number, lunarYear: number, lunarLeap: boolean, timeZone: number): SolarDate | null {
  let a11: number, b11: number;

  if (lunarMonth < 11) {
    a11 = getLunarMonth11(lunarYear - 1, timeZone);
    b11 = getLunarMonth11(lunarYear, timeZone);
  } else {
    a11 = getLunarMonth11(lunarYear, timeZone);
    b11 = getLunarMonth11(lunarYear + 1, timeZone);
  }

  let off = lunarMonth - 11;
  if (off < 0) {
    off += 12;
  }

  if (b11 - a11 > 365) {
    const leapOff = getLeapMonthOffset(a11, timeZone);
    const leapMonth = leapOff - 2;
    if (lunarLeap && lunarMonth !== (leapMonth < 0 ? leapMonth + 12 : leapMonth)) {
      return null; // Ngày nhuận không hợp lệ
    }
    if (lunarLeap || off >= leapOff) {
      off += 1;
    }
  }

  const k = floor(0.5 + (a11 - 2415021.076998695) / 29.530588853);
  const monthStart = getNewMoonDay(k + off, timeZone);

  return computeDateFromJulianDay(monthStart + lunarDay - 1);
}
